import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Flower, User as UserIcon, InstagramLogo, FacebookLogo, TwitterLogo } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Profile() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [socialLinks, setSocialLinks] = useState({
    instagram: '',
    facebook: '',
    twitter: ''
  });

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
        setSocialLinks({
          instagram: response.data.instagram || '',
          facebook: response.data.facebook || '',
          twitter: response.data.twitter || ''
        });
      } catch (error) {
        console.error('Error fetching user:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, []);

  const handleSaveSocialLinks = async () => {
    try {
      setSaving(true);
      const response = await axios.put(
        `${API}/profile/social-links`,
        socialLinks,
        { withCredentials: true }
      );
      setUser(response.data);
      setEditing(false);
      toast.success('Social links updated!');
    } catch (error) {
      console.error('Error updating social links:', error);
      toast.error('Failed to update social links');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-xl font-heading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <nav className="bg-white/70 backdrop-blur-lg border-b border-white/20 px-6 py-4">
        <Button 
          data-testid="back-to-dashboard-button"
          onClick={() => navigate('/dashboard')}
          variant="ghost"
          size="sm"
        >
          ← Back to Dashboard
        </Button>
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-center mb-12">
            <UserIcon size={64} weight="fill" className="text-primary mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">My Profile</h1>
          </div>

          <div className="bg-white/80 backdrop-blur-md border border-white/20 shadow-lg rounded-3xl p-8 md:p-12">
            <div className="flex items-center gap-6 mb-8">
              <img 
                src={user?.picture} 
                alt={user?.name} 
                className="w-24 h-24 rounded-full border-4 border-primary/20"
              />
              <div>
                <h2 className="text-2xl font-heading font-bold mb-1">{user?.name}</h2>
                <p className="text-muted-foreground">{user?.email}</p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-muted rounded-2xl p-6">
                <h3 className="font-heading font-semibold mb-4">Account Information</h3>
                <div className="space-y-2 text-sm">
                  <p><strong>Member Since:</strong> {new Date(user?.created_at).toLocaleDateString()}</p>
                  <p><strong>User ID:</strong> {user?.user_id}</p>
                </div>
              </div>

              <div className="bg-white border border-border rounded-2xl p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-heading font-semibold">Social Media Links</h3>
                  {!editing && (
                    <Button
                      data-testid="edit-social-links-button"
                      onClick={() => setEditing(true)}
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                    >
                      Edit
                    </Button>
                  )}
                </div>

                {editing ? (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="instagram" className="flex items-center gap-2">
                        <InstagramLogo size={20} weight="fill" />
                        Instagram
                      </Label>
                      <Input
                        id="instagram"
                        data-testid="instagram-input"
                        placeholder="@username"
                        value={socialLinks.instagram}
                        onChange={(e) => setSocialLinks({...socialLinks, instagram: e.target.value})}
                        className="rounded-xl"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="facebook" className="flex items-center gap-2">
                        <FacebookLogo size={20} weight="fill" />
                        Facebook
                      </Label>
                      <Input
                        id="facebook"
                        data-testid="facebook-input"
                        placeholder="facebook.com/username"
                        value={socialLinks.facebook}
                        onChange={(e) => setSocialLinks({...socialLinks, facebook: e.target.value})}
                        className="rounded-xl"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="twitter" className="flex items-center gap-2">
                        <TwitterLogo size={20} weight="fill" />
                        Twitter/X
                      </Label>
                      <Input
                        id="twitter"
                        data-testid="twitter-input"
                        placeholder="@username"
                        value={socialLinks.twitter}
                        onChange={(e) => setSocialLinks({...socialLinks, twitter: e.target.value})}
                        className="rounded-xl"
                      />
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button
                        data-testid="cancel-edit-button"
                        onClick={() => {
                          setEditing(false);
                          setSocialLinks({
                            instagram: user?.instagram || '',
                            facebook: user?.facebook || '',
                            twitter: user?.twitter || ''
                          });
                        }}
                        variant="outline"
                        className="flex-1 rounded-full"
                      >
                        Cancel
                      </Button>
                      <Button
                        data-testid="save-social-links-button"
                        onClick={handleSaveSocialLinks}
                        disabled={saving}
                        className="flex-1 rounded-full bg-primary hover:bg-primary-hover"
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {user?.instagram && (
                      <div className="flex items-center gap-3 text-sm">
                        <InstagramLogo size={24} weight="fill" className="text-primary" />
                        <a 
                          href={`https://instagram.com/${user.instagram.replace('@', '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          {user.instagram}
                        </a>
                      </div>
                    )}
                    {user?.facebook && (
                      <div className="flex items-center gap-3 text-sm">
                        <FacebookLogo size={24} weight="fill" className="text-primary" />
                        <a 
                          href={user.facebook.startsWith('http') ? user.facebook : `https://facebook.com/${user.facebook}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          {user.facebook}
                        </a>
                      </div>
                    )}
                    {user?.twitter && (
                      <div className="flex items-center gap-3 text-sm">
                        <TwitterLogo size={24} weight="fill" className="text-primary" />
                        <a 
                          href={`https://twitter.com/${user.twitter.replace('@', '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          {user.twitter}
                        </a>
                      </div>
                    )}
                    {!user?.instagram && !user?.facebook && !user?.twitter && (
                      <p className="text-muted-foreground text-sm">No social media links added yet</p>
                    )}
                  </div>
                )}
              </div>

              <div className="bg-gradient-to-br from-[#FF9A9E] to-[#FECFEF] rounded-2xl p-6 text-white">
                <h3 className="font-heading font-semibold mb-2">Welcome to Bloom & Bond!</h3>
                <p className="text-white/90 text-sm">
                  You're part of a vibrant community of women supporting each other. Keep connecting, sharing, and blooming!
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}